// PivotView — Tabloyu gruplara ayırıp toplamlarla gösterir.
// Kullanım:
//   <PivotView
//     data={rows}
//     columns={[{ id, header, accessor, type }, ...]}  // DataTable column tanımı
//     groupableKeys={['category_name', 'city']}        // Grupla için kullanılabilecek kolonlar (yoksa string olanlar otomatik)
//     valueKeys={['amount', 'quantity']}                // Toplama için number kolonlar (yoksa otomatik bul)
//     defaultGroupBy="category_name"
//     defaultValueKey="amount"
//     defaultAgg="sum"
//   />
import React, { useState, useMemo } from 'react';

const AGG_FN = {
  sum:   (vals) => vals.reduce((a, b) => a + (parseFloat(b) || 0), 0),
  avg:   (vals) => vals.length ? vals.reduce((a, b) => a + (parseFloat(b) || 0), 0) / vals.length : 0,
  count: (vals) => vals.length,
  min:   (vals) => vals.length ? Math.min(...vals.map(v => parseFloat(v) || 0)) : 0,
  max:   (vals) => vals.length ? Math.max(...vals.map(v => parseFloat(v) || 0)) : 0,
};
const AGG_LABEL = { sum: 'Toplam', avg: 'Ortalama', count: 'Adet', min: 'En Düşük', max: 'En Yüksek' };

function getCellValue(row, col) {
  if (typeof col.accessor === 'function') return col.accessor(row);
  if (typeof col.accessor === 'string')   return row[col.accessor];
  return row[col.id];
}

function isNumberLike(v) {
  if (v === null || v === undefined || v === '') return false;
  const n = parseFloat(v);
  return !isNaN(n);
}

function fmt(v, isNum) {
  if (v === null || v === undefined) return '—';
  if (isNum && typeof v === 'number') {
    return v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return String(v);
}

export default function PivotView({
  data = [],
  columns = [],
  groupableKeys,
  valueKeys,
  defaultGroupBy,
  defaultValueKey,
  defaultAgg = 'sum',
  onRowClick,
}) {
  // Otomatik tespit: groupable = string-like, value = number-like (ilk birkaç satıra bakarak)
  const auto = useMemo(() => {
    const sample = data.slice(0, 20);
    const stringKeys = new Set();
    const numberKeys = new Set();
    for (const col of columns) {
      if (col.id === '__select__' || col.hideable === false || col.draggable === false) continue;
      // Tek bir örnekten değer çek
      for (const row of sample) {
        const v = getCellValue(row, col);
        if (v === null || v === undefined || v === '') continue;
        if (isNumberLike(v) && typeof v !== 'string') numberKeys.add(col.id);
        else stringKeys.add(col.id);
        break;
      }
    }
    return { stringKeys: [...stringKeys], numberKeys: [...numberKeys] };
  }, [data, columns]);

  const groupOptions = (groupableKeys && groupableKeys.length ? groupableKeys : auto.stringKeys);
  const valueOptions = (valueKeys && valueKeys.length ? valueKeys : auto.numberKeys);

  const [groupBy, setGroupBy] = useState(defaultGroupBy || groupOptions[0] || '');
  const [valueKey, setValueKey] = useState(defaultValueKey || valueOptions[0] || '');
  const [agg, setAgg] = useState(defaultAgg);
  const [expanded, setExpanded] = useState({}); // { groupValue: bool }
  const [search, setSearch] = useState('');

  const groupCol = columns.find(c => c.id === groupBy);
  const valueCol = columns.find(c => c.id === valueKey);

  // Gruplama
  const groups = useMemo(() => {
    if (!groupBy) return [];
    const map = new Map();
    for (const row of data) {
      const gv = groupCol ? getCellValue(row, groupCol) : row[groupBy];
      const k = gv === null || gv === undefined || gv === '' ? '(Boş)' : String(gv);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(row);
    }
    let result = [...map.entries()].map(([key, rows]) => {
      const vals = valueCol ? rows.map(r => getCellValue(r, valueCol)) : rows.map(r => r[valueKey]);
      const aggregated = AGG_FN[agg] ? AGG_FN[agg](vals.filter(v => v !== null && v !== undefined)) : 0;
      return { key, rows, aggregated, count: rows.length };
    });
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(g => g.key.toLowerCase().includes(q));
    }
    // sıralama: değere göre azalan
    result.sort((a, b) => b.aggregated - a.aggregated);
    return result;
  }, [data, groupBy, groupCol, valueKey, valueCol, agg, search]);

  const grandTotal = useMemo(() => {
    if (!valueCol && !valueKey) return 0;
    const vals = data.map(r => valueCol ? getCellValue(r, valueCol) : r[valueKey])
                     .filter(v => v !== null && v !== undefined);
    return AGG_FN[agg] ? AGG_FN[agg](vals) : 0;
  }, [data, valueKey, valueCol, agg]);

  const grandCount = data.length;
  const maxAgg = groups.length ? Math.max(...groups.map(g => Math.abs(g.aggregated))) || 1 : 1;
  const isNum = agg !== 'count';

  if (groupOptions.length === 0) {
    return (
      <div style={S.empty}>
        Pivot için gruplanabilir kolon bulunamadı. Tabloda string tipinde en az bir kolon olmalı.
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      <div style={S.toolbar}>
        <div style={S.control}>
          <label style={S.lbl}>Grupla</label>
          <select value={groupBy} onChange={e => setGroupBy(e.target.value)} style={S.sel}>
            {groupOptions.map(k => {
              const c = columns.find(c => c.id === k);
              return <option key={k} value={k}>{c?.header || k}</option>;
            })}
          </select>
        </div>
        {valueOptions.length > 0 && (
          <div style={S.control}>
            <label style={S.lbl}>Değer</label>
            <select value={valueKey} onChange={e => setValueKey(e.target.value)} style={S.sel}>
              {valueOptions.map(k => {
                const c = columns.find(c => c.id === k);
                return <option key={k} value={k}>{c?.header || k}</option>;
              })}
            </select>
          </div>
        )}
        <div style={S.control}>
          <label style={S.lbl}>Toplama</label>
          <select value={agg} onChange={e => setAgg(e.target.value)} style={S.sel}>
            {Object.entries(AGG_LABEL).map(([k, lbl]) => (
              <option key={k} value={k}>{lbl}</option>
            ))}
          </select>
        </div>
        <div style={{ ...S.control, marginLeft: 'auto', flex: 1, minWidth: 180 }}>
          <input
            type="text"
            placeholder="Grup ara..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={S.search}
          />
        </div>
      </div>

      <div style={S.summary}>
        <div style={S.summaryItem}>
          <span style={S.summaryLabel}>{groups.length} grup</span>
          <span style={S.summaryDot}>·</span>
          <span style={S.summaryLabel}>{grandCount} kayıt</span>
        </div>
        <div style={S.summaryItem}>
          <span style={S.summaryLabel}>{AGG_LABEL[agg]} ({valueCol?.header || valueKey || '—'}):</span>
          <strong style={S.grandTotal}>{fmt(grandTotal, isNum)}</strong>
        </div>
      </div>

      <div style={S.tableWrap}>
        {groups.length === 0 ? (
          <div style={S.empty}>{search ? 'Arama sonucu yok.' : 'Veri yok.'}</div>
        ) : groups.map(g => {
          const pct = (Math.abs(g.aggregated) / maxAgg) * 100;
          const open = !!expanded[g.key];
          return (
            <div key={g.key} style={S.groupCard}>
              <div style={S.groupHeader} onClick={() => setExpanded(prev => ({ ...prev, [g.key]: !open }))}>
                <div style={S.groupBarBg}>
                  <div style={{ ...S.groupBarFill, width: pct + '%' }} />
                </div>
                <div style={S.groupRow}>
                  <span style={S.expandIcon}>{open ? '▾' : '▸'}</span>
                  <span style={S.groupLabel}>{g.key}</span>
                  <span style={S.groupCount}>{g.count} kayıt</span>
                  <span style={S.groupValue}>{fmt(g.aggregated, isNum)}</span>
                  <span style={S.groupPct}>{((g.aggregated / (grandTotal || 1)) * 100).toFixed(1)}%</span>
                </div>
              </div>
              {open && (
                <div style={S.groupBody}>
                  {g.rows.slice(0, 50).map((row, i) => (
                    <div key={i} style={S.subRow} onClick={() => onRowClick && onRowClick(row)}>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {columns.slice(0, 3).map(c => {
                          if (c.id === groupBy) return null;
                          const v = getCellValue(row, c);
                          if (v == null || v === '') return null;
                          return <span key={c.id} style={S.subCell}><strong>{c.header}:</strong> {String(v)}</span>;
                        })}
                      </span>
                      {valueCol && (
                        <span style={S.subValue}>{fmt(getCellValue(row, valueCol), true)}</span>
                      )}
                    </div>
                  ))}
                  {g.rows.length > 50 && (
                    <div style={{ ...S.subRow, justifyContent: 'center', color: 'var(--text-tertiary)' }}>
                      ...+{g.rows.length - 50} daha
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const S = {
  wrap: { display: 'flex', flexDirection: 'column', height: '100%' },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    padding: '10px 12px',
    borderBottom: '1px solid var(--border-secondary)',
    background: 'var(--bg-card)',
  },
  control: { display: 'flex', alignItems: 'center', gap: 6 },
  lbl: {
    fontSize: 'var(--text-xs)', fontWeight: 600,
    color: 'var(--text-tertiary)', textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  sel: {
    padding: '4px 8px',
    fontSize: 'var(--text-sm)',
    border: '1px solid var(--border-input)',
    borderRadius: 'var(--r-sm)',
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
    minWidth: 130,
  },
  search: {
    width: '100%', padding: '4px 10px',
    fontSize: 'var(--text-sm)',
    border: '1px solid var(--border-input)',
    borderRadius: 'var(--r-sm)',
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
  },
  summary: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 14px',
    background: 'var(--bg-secondary)',
    borderBottom: '1px solid var(--border-secondary)',
    flexWrap: 'wrap', gap: 8,
  },
  summaryItem: { display: 'flex', alignItems: 'center', gap: 6 },
  summaryLabel: { fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' },
  summaryDot: { color: 'var(--text-tertiary)' },
  grandTotal: { fontSize: 'var(--text-lg)', color: 'var(--accent-primary)', fontFamily: 'JetBrains Mono, monospace' },
  tableWrap: {
    flex: 1, overflowY: 'auto',
    padding: 8,
    background: 'var(--bg-primary)',
  },
  groupCard: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border-secondary)',
    borderRadius: 'var(--r-md)',
    marginBottom: 6,
    overflow: 'hidden',
    boxShadow: 'var(--shadow-xs)',
  },
  groupHeader: {
    position: 'relative',
    cursor: 'pointer',
    padding: '10px 12px',
  },
  groupBarBg: {
    position: 'absolute', inset: 0,
    background: 'var(--bg-card)',
  },
  groupBarFill: {
    position: 'absolute', left: 0, top: 0, bottom: 0,
    background: 'var(--accent-glow)',
    transition: 'width 0.2s',
  },
  groupRow: {
    position: 'relative',
    display: 'flex', alignItems: 'center', gap: 10,
    fontSize: 'var(--text-sm)',
  },
  expandIcon: { color: 'var(--text-tertiary)', width: 14 },
  groupLabel: { fontWeight: 600, color: 'var(--text-primary)', flex: 1 },
  groupCount: { fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' },
  groupValue: {
    fontFamily: 'JetBrains Mono, monospace',
    fontWeight: 600,
    color: 'var(--text-primary)',
    minWidth: 110, textAlign: 'right',
  },
  groupPct: {
    fontSize: 'var(--text-xs)',
    color: 'var(--accent-primary)',
    fontWeight: 600,
    minWidth: 50, textAlign: 'right',
  },
  groupBody: {
    borderTop: '1px solid var(--border-secondary)',
    background: 'var(--bg-card-hover)',
  },
  subRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '6px 12px',
    fontSize: 'var(--text-xs)',
    color: 'var(--text-secondary)',
    borderBottom: '1px solid var(--border-secondary)',
    cursor: 'pointer',
  },
  subCell: { marginRight: 10 },
  subValue: {
    fontFamily: 'JetBrains Mono, monospace',
    fontWeight: 600,
    color: 'var(--text-primary)',
    minWidth: 100, textAlign: 'right',
  },
  empty: {
    padding: 30, textAlign: 'center',
    color: 'var(--text-tertiary)',
    fontSize: 'var(--text-sm)',
    fontStyle: 'italic',
  },
};
